import { z as t } from "zod";
import { ocPrivate as r, ocPublic as s } from "../common/auth/procedure-builders.js";
import "../common/errors/constants.js";
import { requestValidationError as a, authRejectedError as u } from "../common/errors/contract.js";
import "../common/errors/schemas.js";
import "../swagger/security.js";
import { SwaggerTags as e } from "../swagger/tags.js";
import { RefreshTokenRequestSchema as m, TokenResponseSchema as i, LoginCredentialsSchema as p, MessageResponseSchema as o, ChangePasswordSchema as c, UpdateProfileSchema as d, ProfileSchema as h, CaptchaConfigSchema as S, PasswordResetSchema as T, PasswordResetRequestSchema as l, RegistrationSchema as g, ImpersonationUserSchema as f, StartImpersonationRequestSchema as y, StartImpersonationResultSchema as b, SessionRoleRequestSchema as U, SessionUserSchema as n, SessionLoginSchema as w } from "./schemas.js";
const L = {
  session: {
    /**
     * Cookie-based login endpoint
     * Calls external auth API and sets session cookie
     *
     * Note: No UNAUTHORIZED error in contract - invalid credentials handled by implementation.
     * This keeps the endpoint unprotected (no auth required to call login).
     */
    create: s().route({
      method: "POST",
      path: "/auth/session",
      summary: "Login and set session cookie",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: w
      })
    ).output(o).errors({
      ...u("The auth service refused the credentials or the captcha")
    }),
    /**
     * Get current authenticated user from session
     * Public: null when the session cookie is missing or expired.
     */
    get: s().route({
      method: "GET",
      path: "/auth/session",
      summary: "Get current session user",
      tags: [e.AUTH.name]
    }).input(t.object({})).output(n.nullable()).errors({}),
    /**
     * Logout endpoint
     * Removes user session and clears cookie
     * Idempotent — succeeds even without authentication.
     */
    remove: s().route({
      method: "DELETE",
      path: "/auth/session",
      summary: "Logout and clear session",
      tags: [e.AUTH.name]
    }).input(t.object({})).output(o).errors({}),
    /**
     * Switches the active role stored in the session.
     * The role must be one of the session user's roles.
     */
    setRole: r().route({
      method: "PUT",
      path: "/auth/session/role",
      summary: "Switch the active role of the session",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: U
      })
    ).output(n).errors({}),
    /**
     * Opens a new session as another user in a free account slot; the caller's own session stays untouched.
     * No privilege list: it would be filtered by the active role, so the handler checks the user's roles itself.
     */
    impersonate: r().route({
      method: "POST",
      path: "/auth/session/impersonation",
      summary: "Log in as another user in a new account slot",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: y
      })
    ).output(b).errors({}),
    /**
     * Users the session user may start acting as.
     */
    impersonationUsers: r().route({
      method: "GET",
      path: "/auth/session/impersonation/users",
      summary: "List users available for impersonation",
      tags: [e.AUTH.name]
    }).input(t.object({})).output(t.array(f)).errors({})
  },
  registration: {
    create: s().route({
      method: "POST",
      path: "/auth/registration",
      summary: "Sign up and set session cookie",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: g
      })
    ).output(o).errors({
      ...a("The auth service refused the registration data"),
      ...u("The auth service refused the captcha")
    })
  },
  passwordReset: {
    request: s().route({
      method: "POST",
      path: "/auth/password-reset",
      summary: "Send a password reset link to the email",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: l
      })
    ).output(o).errors({
      ...a("The auth service refused the email"),
      ...u("The auth service refused the captcha")
    }),
    confirm: s().route({
      method: "POST",
      path: "/auth/password-reset/confirm",
      summary: "Set a new password with the token from the reset link",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: T
      })
    ).output(o).errors({
      ...a("The auth service refused the token or the password"),
      ...u("The auth service refused the request")
    })
  },
  captcha: {
    get: s().route({
      method: "GET",
      path: "/auth/captcha",
      summary: "Get the public captcha settings",
      tags: [e.AUTH.name]
    }).input(t.object({})).output(S).errors({})
  },
  profile: {
    get: r().route({
      method: "GET",
      path: "/auth/profile",
      summary: "Get the session user profile",
      tags: [e.AUTH.name]
    }).output(h).errors({}),
    update: r().route({
      method: "PUT",
      path: "/auth/profile",
      summary: "Save the session user profile",
      tags: [e.AUTH.name]
    }).input(d).output(h).errors({}),
    changePassword: r().route({
      method: "PUT",
      path: "/auth/profile/password",
      summary: "Change the session user password",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: c
      })
    ).output(o).errors({
      ...a("The auth service refused the old or the new password")
    }),
    endAllSessions: r().route({
      method: "DELETE",
      path: "/auth/profile/sessions",
      summary: "Log the session user out of every device",
      tags: [e.AUTH.name]
    }).output(o).errors({})
  },
  token: {
    /**
     * OAuth2 password grant token endpoint
     * Calls external auth API and returns Bearer token
     *
     * Note: No UNAUTHORIZED error in contract - invalid credentials handled by implementation.
     * This keeps the endpoint unprotected (no auth required to get token).
     */
    create: s().route({
      method: "POST",
      path: "/auth/token",
      summary: "Get access token (OAuth2)",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: p
      })
    ).output(i).errors({}),
    /**
     * Refresh access token
     * Calls external auth API to refresh the token
     * Requires authentication (Bearer token in Authorization header).
     * OAuth2 RFC 6749 Section 6 compliant.
     */
    refresh: r().route({
      method: "PUT",
      path: "/auth/token",
      summary: "Refresh access token (OAuth2)",
      tags: [e.AUTH.name],
      inputStructure: "detailed"
    }).input(
      t.object({
        body: m
      })
    ).output(i).errors({})
  }
};
export {
  L as authContract
};
//# sourceMappingURL=contract.js.map
